import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone } from "lucide-react";
import axios from "axios";

export default function TechnicianLogin() {
  const nav = useNavigate();
  const { loginTechnician } = useAuth();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [stationsLoading, setStationsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/stations/public`);
        setStations(data || []);
        if (data?.length) setStationId(data[0].id);
      } catch { toast.error("Could not load stations"); }
      finally { setStationsLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!stationId) return toast.error("Please select a station");
    setLoading(true);
    try {
      await loginTechnician(stationId, pin);
      toast.success(`${stationId} unlocked`);
      nav("/technician");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid PIN");
    } finally { setLoading(false); }
  };

  const selected = stations.find((s) => s.id === stationId);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-10"><BrandMark subtitle={selected?.id || "TECHNICIAN"} /></div>
        <div className="p-8 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="overline mb-2">Technician</div>
          <h1 className="font-heading text-3xl mb-6">Enter station PIN</h1>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="station" className="overline">Station</Label>
              {stationsLoading ? (
                <div className="mt-2 h-11 flex items-center gap-2 text-white/50 text-sm"><Loader2 className="animate-spin" size={14}/> Loading stations…</div>
              ) : stations.length === 0 ? (
                <div className="mt-2 h-11 flex items-center gap-2 text-white/50 text-sm">No active stations configured.</div>
              ) : (
                <div className="mt-2 grid gap-2" data-testid="technician-station-picker">
                  {stations.map((s) => {
                    const on = s.id === stationId;
                    return (
                      <button type="button" key={s.id} onClick={() => setStationId(s.id)} data-testid={`technician-station-opt-${s.id}`}
                        className={`h-11 rounded-sm text-[13px] font-medium tracking-tight border transition-colors flex items-center gap-2 px-3 ${on ? "text-white" : "text-white/60 hover:text-white/90"}`}
                        style={{ background: on ? "rgba(0,229,255,0.10)" : "rgba(255,255,255,0.02)", borderColor: on ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.10)" }}>
                        <MonitorSmartphone size={14} style={{ color: on ? "var(--accent)" : undefined }}/>
                        <span className="tracking-widest font-mono text-[12px]">{s.id}</span>
                        <span className="text-white/50 font-normal ml-1 truncate">{s.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="pin" className="overline">Station PIN</Label>
              <Input id="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} required
                data-testid="technician-pin-input"
                className="mt-2 bg-black border-white/20 text-white h-14 text-2xl font-mono tracking-widest text-center" autoFocus/>
            </div>
            <Button type="submit" disabled={loading || !stationId || stations.length === 0} data-testid="technician-login-submit"
              className="w-full h-11 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }}>
              {loading ? "Unlocking…" : `Unlock ${stationId || "Station"}`}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
